import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, Loader2, ChefHat, Star } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    if (params.get("provider") === "razorpay") { setStatus("paid"); return; }
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let active = true;
    const poll = async (n) => {
      if (!active || n > 8) { if (active) setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("cancelled"); return; }
      } catch {}
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { active = false; };
  }, [isCancel, location.search]);

  const submitFeedback = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    if (!phone) return toast.error("Enter your phone so we can link your review");
    try {
      const slug = new URLSearchParams(location.search).get("slug");
      if (!slug) { toast.error("Missing restaurant link"); return; }
      await api.post(`/r/${slug}/feedback`, { phone, rating, comment });
      setSent(true); toast.success("Thanks for your feedback!");
    } catch { toast.error("Could not submit feedback"); }
  };

  const config = {
    checking: { icon: Loader2, color: "#FF6414", title: "Confirming payment…", sub: `Please wait a moment (${attempts}/8)`, spin: true },
    paid: { icon: CheckCircle2, color: "#34C759", title: "Payment Successful!", sub: "Your order has been placed. The kitchen is on it!" },
    cancelled: { icon: XCircle, color: "#FF3B30", title: "Payment Cancelled", sub: "No charge was made. You can try ordering again." },
    timeout: { icon: Loader2, color: "#FFCC00", title: "Still processing", sub: "Your payment is taking longer than usual." },
    error: { icon: XCircle, color: "#FF3B30", title: "Something went wrong", sub: "We couldn't find your payment session." },
  }[status];
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--kul-app-bg)] p-6">
      <div className="kul-card p-10 max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-5" style={{ background: `${config.color}1a` }}>
          <Icon className={`w-9 h-9 ${config.spin ? "animate-spin" : ""}`} style={{ color: config.color }} />
        </div>
        <h1 className="text-2xl font-700 tracking-tight" data-testid="payment-status-title">{config.title}</h1>
        <p className="text-[#86868b] mt-2">{config.sub}</p>

        {status === "paid" && !sent && (
          <div className="mt-7 pt-6 border-t text-left" data-testid="feedback-form">
            <h3 className="font-semibold text-center">How was your experience?</h3>
            <div className="flex justify-center gap-1.5 mt-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)} data-testid={`star-${i}`}>
                  <Star className={`w-8 h-8 transition-transform active:scale-90 ${i <= (hover || rating) ? "fill-[#FFCC00] text-[#FFCC00]" : "text-neutral-300"}`} />
                </button>
              ))}
            </div>
            <Input className="mt-4" placeholder="Your phone number" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="feedback-phone" />
            <Textarea className="mt-2" rows={2} placeholder="Tell us more (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="feedback-comment" />
            <Button onClick={submitFeedback} data-testid="submit-feedback" className="w-full mt-3 bg-[var(--kul-primary)] text-white">Submit Review</Button>
          </div>
        )}
        {sent && <p className="mt-6 text-[#34C759] font-medium">⭐ Review submitted — thank you!</p>}

        <Button onClick={() => {
          const slug = new URLSearchParams(location.search).get("slug");
          navigate(slug ? `/r/${slug}/order` : "/");
        }} data-testid="back-to-store" variant={status === "paid" ? "outline" : "default"}
          className={`mt-6 ${status === "paid" ? "" : "bg-[var(--kul-primary)] text-white"}`}>
          <ChefHat className="w-4 h-4 mr-1" /> Back to Menu
        </Button>
      </div>
    </div>
  );
}
